import React, { useState, useEffect } from 'react';
import SchoolFilter, { createSchoolKey } from './SchoolFilter';
import AddressLookup from './AddressLookup';

const Sidebar = ({
  // Core state
  currentYear,
  yearData,
  visibleLayers,
  onToggleLayer,
  
  // School filtering
  selectedSchoolNames,
  selectedSchools,
  isFiltering,
  onSchoolsFilter,
  onClearFilter,
  
  // Color coding
  colorBySchool,
  onToggleColorMode,
  schoolColorMap,
  schoolColors,
  
  // Map reference
  map
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState('layers');
  
  // Detect mobile devices
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const userAgent = navigator.userAgent;
      const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      
      setIsMobile(width <= 768 || isMobileUserAgent);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (!isMobile) return;

    const handleClickOutside = (e) => {
      if (mobileMenuOpen && !e.target.closest('.mobile-sidebar-menu') && !e.target.closest('.mobile-sidebar-trigger')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobile, mobileMenuOpen]);
  
  // Calculate dynamic sidebar width for desktop
  const sidebarWidth = sidebarCollapsed ? 60 : 340;

  // MOBILE: Top Bar Component
  if (isMobile) {
    return (
      <>
        {/* MOBILE: Floating Top Bar */}
        <div style={{
          position: 'absolute',
          top: '70px', // Below the main header
          left: '16px',
          right: '16px',
          height: '56px',
          background: 'linear-gradient(135deg, rgba(15, 20, 25, 0.95) 0%, rgba(26, 32, 44, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          zIndex: 15,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          transition: 'all 0.3s ease'
        }}>
          {/* Left: Status Indicators */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            {/* Filter Status */}
            {isFiltering && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '8px'
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#10B981',
                  animation: 'pulse 2s ease-in-out infinite'
                }} />
                <span style={{
                  color: '#34D399',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  {selectedSchools.length} Selected
                </span>
              </div>
            )}

            {/* Color Mode Indicator */}
            {colorBySchool && selectedSchools.length > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 8px',
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.08) 100%)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px'
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#A78BFA">
                  <path d="M12,3C16.97,3 21,7.03 21,12C21,17 16.97,21 12,21C7.03,21 3,17 3,12C3,7.03 7.03,3 12,3M12,5C10.89,5 9.85,5.26 8.92,5.74L15.26,12.08C15.74,11.15 16,10.11 16,9C16,6.79 14.21,5 12,5Z"/>
                </svg>
                <span style={{
                  color: '#A78BFA',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  Colors
                </span>
              </div>
            )}
          </div>

          {/* Right: Quick Controls + Menu Button */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {/* Quick Layer Toggles */}
            <div style={{
              display: 'flex',
              gap: '4px',
              padding: '4px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.08)'
            }}>
              {[
                { key: 'elementary', color: '#3B82F6', icon: 'E' },
                { key: 'highSchool', color: '#EF4444', icon: 'H' },
                { key: 'middleSchool', color: '#10B981', icon: 'M' }
              ].map(({ key, color, icon }) => (
                <button
                  key={key}
                  onClick={() => !isFiltering && onToggleLayer(key)}
                  disabled={isFiltering}
                  style={{
                    width: '28px',
                    height: '28px',
                    background: visibleLayers[key] ? 
                      (isFiltering ? '#6B7280' : `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`) : 
                      'transparent',
                    border: `1px solid ${visibleLayers[key] ? 
                      (isFiltering ? '#6B7280' : color) : 
                      'rgba(255, 255, 255, 0.15)'}`,
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isFiltering ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    color: visibleLayers[key] ? 'white' : 'rgba(255, 255, 255, 0.6)',
                    fontSize: '10px',
                    fontWeight: '700',
                    opacity: isFiltering ? 0.5 : 1,
                    boxShadow: visibleLayers[key] && !isFiltering ? 
                      `0 2px 8px ${color}40` : 
                      'none'
                  }}
                >
                  {icon}
                </button>
              ))}
            </div>

            {/* Menu Button */}
            <button
              className="mobile-sidebar-trigger"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                background: mobileMenuOpen ?
                  'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' :
                  'rgba(255, 255, 255, 0.1)',
                border: `1px solid ${mobileMenuOpen ? '#3B82F6' : 'rgba(255, 255, 255, 0.15)'}`,
                borderRadius: '10px',
                padding: '10px',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                boxShadow: mobileMenuOpen ?
                  '0 4px 12px rgba(59, 130, 246, 0.3)' :
                  '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              <svg 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                style={{
                  transform: mobileMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}
              >
                <path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z"/>
              </svg>
            </button>
          </div>
        </div>

        {/* MOBILE: Expandable Menu Panel */}
        {mobileMenuOpen && (
          <div 
            className="mobile-sidebar-menu"
            style={{
              position: 'absolute',
              top: '136px', // Below the top bar
              left: '16px',
              right: '16px',
              background: 'linear-gradient(135deg, rgba(15, 20, 25, 0.98) 0%, rgba(26, 32, 44, 0.98) 100%)',
              backdropFilter: 'blur(32px)',
              borderRadius: '20px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.6)',
              zIndex: 14,
              maxHeight: 'calc(100vh - 220px)',
              overflowY: 'auto',
              transform: mobileMenuOpen ? 'translateY(0) scale(1)' : 'translateY(-20px) scale(0.95)',
              opacity: mobileMenuOpen ? 1 : 0,
              transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
              transformOrigin: 'top center'
            }}
          >
            {/* Tab Navigation */}
            <div style={{
              padding: '20px 20px 16px 20px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
              position: 'sticky',
              top: 0,
              background: 'inherit',
              backdropFilter: 'inherit',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px'
            }}>
              <div style={{
                display: 'flex',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '4px',
                gap: '4px'
              }}>
                {[
                  { 
                    id: 'layers', 
                    label: 'Layers', 
                    icon: 'M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z',
                    badge: isFiltering ? '!' : null
                  },
                  { 
                    id: 'search', 
                    label: 'Schools', 
                    icon: 'M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z',
                    badge: selectedSchools.length > 0 ? selectedSchools.length : null
                  },
                  { 
                    id: 'address', 
                    label: 'Address', 
                    icon: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'
                  }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setMobileActiveTab(tab.id)}
                    style={{
                      flex: 1,
                      padding: '12px 8px',
                      background: mobileActiveTab === tab.id ? 
                        'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%)' : 
                        'transparent',
                      border: mobileActiveTab === tab.id ? 
                        '1px solid rgba(59, 130, 246, 0.3)' : 
                        '1px solid transparent',
                      borderRadius: '8px',
                      color: mobileActiveTab === tab.id ? '#60A5FA' : 'rgba(255, 255, 255, 0.6)',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                      position: 'relative'
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d={tab.icon}/>
                      </svg>
                      {tab.badge && (
                        <div style={{
                          position: 'absolute',
                          top: '-6px',
                          right: '-8px',
                          background: tab.id === 'layers' ? '#EF4444' : '#10B981',
                          color: 'white',
                          borderRadius: '10px',
                          padding: '2px 6px',
                          fontSize: '8px',
                          fontWeight: '700',
                          minWidth: '16px',
                          textAlign: 'center',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                        }}>
                          {tab.badge}
                        </div>
                      )}
                    </div>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div style={{ padding: '0 20px 20px 20px' }}>
              {/* Layers Tab */}
              {mobileActiveTab === 'layers' && (
                <div>
                  <h4 style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '16px',
                    fontWeight: '600',
                    margin: '16px 0 16px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#8B5CF6">
                      <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
                    </svg>
                    Layer Visibility
                  </h4>
                  
                  {isFiltering && (
                    <div style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="#F87171">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
                      </svg>
                      <div>
                        <div style={{
                          color: '#F87171',
                          fontSize: '12px',
                          fontWeight: '600',
                          marginBottom: '2px'
                        }}>
                          Layer controls disabled
                        </div>
                        <div style={{
                          color: 'rgba(255, 255, 255, 0.6)',
                          fontSize: '11px'
                        }}>
                          Clear school filter to enable layer controls
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      { key: 'elementary', label: 'Elementary Schools', color: '#3B82F6', icon: 'E' },
                      { key: 'highSchool', label: 'High Schools', color: '#EF4444', icon: 'H' },
                      { key: 'middleSchool', label: 'Middle Schools', color: '#10B981', icon: 'M' }
                    ].map(({ key, label, color, icon }) => (
                      <label key={key} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: isFiltering ? 'not-allowed' : 'pointer',
                        padding: '16px',
                        borderRadius: '12px',
                        background: visibleLayers[key] ? 
                          `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)` : 
                          'rgba(255, 255, 255, 0.03)',
                        border: `1px solid ${visibleLayers[key] ? `${color}40` : 'rgba(255, 255, 255, 0.08)'}`,
                        opacity: isFiltering ? 0.5 : 1,
                        transition: 'all 0.3s ease'
                      }}>
                        <input
                          type="checkbox"
                          checked={visibleLayers[key]}
                          onChange={() => !isFiltering && onToggleLayer(key)}
                          disabled={isFiltering}
                          style={{ display: 'none' }}
                        />
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '6px',
                          background: visibleLayers[key] ? 
                            (isFiltering ? '#6B7280' : `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`) : 
                            'rgba(255, 255, 255, 0.1)',
                          border: `2px solid ${visibleLayers[key] ? 
                            (isFiltering ? '#6B7280' : color) : 
                            'rgba(255, 255, 255, 0.2)'}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '16px'
                        }}>
                          {visibleLayers[key] && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                              <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                            </svg>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: isFiltering ? 
                              'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)' : 
                              `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '700',
                            boxShadow: `0 4px 8px ${isFiltering ? '#6B728040' : color + '40'}`
                          }}>
                            {icon}
                          </div>
                          <span style={{ 
                            color: isFiltering ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.9)', 
                            fontSize: '16px', 
                            fontWeight: '600'
                          }}>
                            {label}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* School Search Tab */}
              {mobileActiveTab === 'search' && (
                <div>
                  <h4 style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '16px',
                    fontWeight: '600',
                    margin: '16px 0 16px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#10B981">
                      <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
                    </svg>
                    School Search & Filter
                  </h4>
                  
                  <SchoolFilter
                    currentYear={currentYear}
                    yearData={yearData}
                    selectedSchoolNames={selectedSchoolNames}
                    onSchoolsFilter={onSchoolsFilter}
                    onClearFilter={onClearFilter}
                    sidebar={true}
                    mobile={true}
                  />

                  {/* Color Mode Toggle for Mobile */}
                  {selectedSchools.length >= 2 && selectedSchools.length <= 12 && (
                    <div style={{
                      marginTop: '20px',
                      padding: '16px',
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(139, 92, 246, 0.03) 100%)',
                      border: '1px solid rgba(139, 92, 246, 0.15)',
                      borderRadius: '12px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '12px'
                      }}>
                        <div>
                          <div style={{
                            fontSize: '14px',
                            color: 'rgba(139, 92, 246, 0.9)',
                            fontWeight: '600',
                            marginBottom: '4px'
                          }}>
                            Color Coding
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: 'rgba(255, 255, 255, 0.6)',
                            lineHeight: '1.3'
                          }}>
                            {colorBySchool ? `Individual colors for ${selectedSchools.length} schools` : 'Standard colors by type'}
                          </div>
                        </div>
                        <button
                          onClick={onToggleColorMode}
                          style={{
                            background: colorBySchool ? 
                              'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)' : 
                              'rgba(255, 255, 255, 0.08)',
                            border: `1px solid ${colorBySchool ? '#8B5CF6' : 'rgba(255, 255, 255, 0.15)'}`,
                            borderRadius: '8px',
                            color: colorBySchool ? 'white' : 'rgba(255, 255, 255, 0.8)',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            padding: '8px 14px',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12,3C16.97,3 21,7.03 21,12C21,17 16.97,21 12,21C7.03,21 3,17 3,12C3,7.03 7.03,3 12,3M12,5C10.89,5 9.85,5.26 8.92,5.74L15.26,12.08C15.74,11.15 16,10.11 16,9C16,6.79 14.21,5 12,5Z"/>
                          </svg>
                          {colorBySchool ? 'School Colors' : 'Type Colors'}
                        </button>
                      </div>

                      {/* Color Preview for Mobile */}
                      {colorBySchool && selectedSchools.length > 0 && (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                          gap: '8px',
                          maxHeight: '120px',
                          overflowY: 'auto'
                        }}>
                          {selectedSchools.slice(0, 6).map((school, index) => {
                            const schoolKey = school.schoolKey || createSchoolKey(school.name, school.type);
                            const color = schoolColorMap.get(schoolKey) || schoolColors[index % schoolColors.length];
                            return (
                              <div key={school.id} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 8px',
                                borderRadius: '6px',
                                background: 'rgba(255, 255, 255, 0.03)',
                                border: '1px solid rgba(255, 255, 255, 0.05)'
                              }}>
                                <div style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  background: color,
                                  flexShrink: 0
                                }}></div>
                                <span style={{
                                  fontSize: '9px',
                                  color: 'rgba(255, 255, 255, 0.8)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: 1
                                }}>
                                  {school.name}
                                </span>
                              </div>
                            );
                          })}
                          {selectedSchools.length > 6 && (
                            <div style={{
                              fontSize: '9px',
                              color: 'rgba(255, 255, 255, 0.5)',
                              textAlign: 'center',
                              padding: '6px',
                              fontStyle: 'italic'
                            }}>
                              +{selectedSchools.length - 6} more
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Address Tab */}
              {mobileActiveTab === 'address' && (
                <div>
                  <h4 style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '16px',
                    fontWeight: '600',
                    margin: '16px 0 16px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#F59E0B">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    Address Lookup
                  </h4>
                  
                  <AddressLookup
                    map={map}
                    currentYear={currentYear}
                    yearData={yearData}
                    selectedSchoolNames={selectedSchoolNames}
                    isFiltering={isFiltering}
                    onAddressFound={(marker, assignments) => {
                      console.log('Address found:', marker, assignments);
                    }}
                    onAddressCleared={() => {
                      console.log('Address cleared');
                    }}
                    sidebar={true}
                    mobile={true}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add pulse animation */}
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </>
    );
  }

  // DESKTOP: Original Sidebar Layout (COMPLETE IMPLEMENTATION)
  return (
    <>
      {/* PREMIUM: Enhanced Collapsible Sidebar with glassmorphism */}
      <div style={{
        position: 'absolute',
        top: '64px',
        left: '0',
        bottom: '0',
        width: `${sidebarWidth}px`,
        background: 'linear-gradient(145deg, rgba(15, 20, 25, 0.97) 0%, rgba(26, 32, 44, 0.97) 50%, rgba(15, 20, 25, 0.97) 100%)',
        backdropFilter: 'blur(32px) saturate(180%)',
        borderRight: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: `
          12px 0 40px rgba(0, 0, 0, 0.4),
          inset 1px 0 0 rgba(255, 255, 255, 0.03),
          inset 0 1px 0 rgba(255, 255, 255, 0.02)
        `,
        zIndex: 5,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'width 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        willChange: 'width'
      }}>
        
        {/* PREMIUM: Ambient Background Glow */}
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: `
            radial-gradient(ellipse at top, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
            radial-gradient(ellipse at center, rgba(139, 92, 246, 0.02) 0%, transparent 70%),
            radial-gradient(ellipse at bottom, rgba(16, 185, 129, 0.02) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
          opacity: sidebarCollapsed ? 0 : 1,
          transition: 'opacity 0.4s ease'
        }} />

        {/* PREMIUM: Floating Toggle Button with Enhanced Design */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: sidebarCollapsed ? '12px' : '20px',
          zIndex: 20,
          transition: 'right 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
        }}>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{
              width: '40px',
              height: '40px',
              background: `
                linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(29, 78, 216, 0.9) 100%),
                radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.2) 0%, transparent 70%)
              `,
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              boxShadow: `
                0 8px 25px rgba(59, 130, 246, 0.35),
                0 3px 12px rgba(0, 0, 0, 0.3),
                inset 0 1px 0 rgba(255, 255, 255, 0.2)
              `,
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.08) translateY(-2px)';
              e.target.style.boxShadow = `
                0 12px 35px rgba(59, 130, 246, 0.45),
                0 6px 20px rgba(0, 0, 0, 0.4),
                inset 0 1px 0 rgba(255, 255, 255, 0.3)
              `;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1) translateY(0px)';
              e.target.style.boxShadow = `
                0 8px 25px rgba(59, 130, 246, 0.35),
                0 3px 12px rgba(0, 0, 0, 0.3),
                inset 0 1px 0 rgba(255, 255, 255, 0.2)
              `;
            }}
            title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            {/* Shimmer Effect */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)',
              transform: sidebarCollapsed ? 'translateX(100%)' : 'translateX(-100%)',
              transition: 'transform 0.6s ease',
              pointerEvents: 'none'
            }} />
            
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="currentColor"
              style={{
                transform: sidebarCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
              }}
            >
              <path d="M15.41 16.09l-4.58-4.59 4.58-4.59L14 5.5l-6 6 6 6z"/>
            </svg>
          </button>
        </div>

        {/* PREMIUM: Enhanced Header with Depth */}
        <div style={{
          padding: sidebarCollapsed ? '20px 12px 20px 12px' : '24px 24px 20px 24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          background: `
            linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.01) 100%),
            linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.02) 100%)
          `,
          flexShrink: 0,
          transition: 'padding 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
          marginTop: sidebarCollapsed ? '50px' : '0',
          position: 'relative'
        }}>
          {/* Subtle top border highlight */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.03) 50%, transparent 100%)'
          }} />
          
          {sidebarCollapsed ? (
            // Collapsed state - enhanced icon with glow
            <div style={{
              display: 'flex',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                background: `
                  linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%),
                  radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.2) 0%, transparent 70%)
                `,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `
                  0 8px 25px rgba(139, 92, 246, 0.4),
                  0 3px 12px rgba(0, 0, 0, 0.3),
                  inset 0 1px 0 rgba(255, 255, 255, 0.2)
                `,
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white" style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                }}>
                  <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
                </svg>
              </div>
            </div>
          ) : (
            // Expanded state - premium header with enhanced typography
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              <div style={{
                width: '44px',
                height: '44px',
                background: `
                  linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%),
                  radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.2) 0%, transparent 70%)
                `,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `
                  0 8px 25px rgba(139, 92, 246, 0.4),
                  0 3px 12px rgba(0, 0, 0, 0.3),
                  inset 0 1px 0 rgba(255, 255, 255, 0.2)
                `,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Icon shimmer effect */}
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  left: '-50%',
                  width: '200%',
                  height: '200%',
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)',
                  animation: 'shimmer 3s ease-in-out infinite',
                  pointerEvents: 'none'
                }} />
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white" style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
                </svg>
              </div>
              <div>
                <h3 style={{ 
                  margin: '0', 
                  fontSize: '18px', 
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #ffffff 0%, #e5e7eb 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '-0.02em',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  Controls
                </h3>
                <p style={{
                  margin: '4px 0 0 0',
                  fontSize: '13px',
                  color: 'rgba(255, 255, 255, 0.65)',
                  fontWeight: '500',
                  letterSpacing: '0.01em'
                }}>
                  Map settings & filters
                </p>
              </div>
            </div>
          )}
          
          <style>{`
            @keyframes shimmer {
              0% { transform: translateX(-100%) rotate(45deg); }
              50% { transform: translateX(-100%) rotate(45deg); }
              100% { transform: translateX(100%) rotate(45deg); }
            }
          `}</style>
        </div>

        {/* PREMIUM: Scrollable Content with Enhanced Sections */}
        <div style={{
          flex: 1,
          overflowY: sidebarCollapsed ? 'hidden' : 'auto',
          overflowX: 'hidden',
          padding: sidebarCollapsed ? '0' : '0',
          opacity: sidebarCollapsed ? 0 : 1,
          transition: 'opacity 0.3s ease, padding 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
          pointerEvents: sidebarCollapsed ? 'none' : 'auto',
          // Custom scrollbar
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.1) transparent'
        }}>
          
          {/* PREMIUM: School Filter Section with Glass Card */}
          <div style={{
            margin: '24px 20px 16px 20px',
            padding: '20px',
            background: `
              linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%),
              linear-gradient(to bottom, rgba(59, 130, 246, 0.02) 0%, transparent 70%)
            `,
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            boxShadow: `
              0 8px 32px rgba(0, 0, 0, 0.2),
              inset 0 1px 0 rgba(255, 255, 255, 0.03)
            `,
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Card glow effect */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.3) 50%, transparent 100%)'
            }} />
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                  <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
                </svg>
              </div>
              <div>
                <h4 style={{
                  margin: '0',
                  fontSize: '16px',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #ffffff 0%, #e5e7eb 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  School Search
                </h4>
                <p style={{
                  margin: '2px 0 0 0',
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontWeight: '400'
                }}>
                  Filter by school name
                </p>
              </div>
            </div>
            
            <SchoolFilter
              currentYear={currentYear}
              yearData={yearData}
              selectedSchoolNames={selectedSchoolNames}
              onSchoolsFilter={onSchoolsFilter}
              onClearFilter={onClearFilter}
              sidebar={true}
            />

            {/* PREMIUM: Enhanced Color Mode Toggle */}
            {selectedSchools.length >= 2 && selectedSchools.length <= 12 && (
              <div style={{
                marginTop: '20px',
                padding: '16px',
                background: `
                  linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(139, 92, 246, 0.03) 100%),
                  linear-gradient(to bottom, rgba(255, 255, 255, 0.01) 0%, transparent 100%)
                `,
                border: '1px solid rgba(139, 92, 246, 0.15)',
                borderRadius: '12px',
                backdropFilter: 'blur(8px)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Animated background gradient */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `
                    radial-gradient(circle at 20% 30%, rgba(139, 92, 246, 0.03) 0%, transparent 50%),
                    radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.02) 0%, transparent 50%)
                  `,
                  pointerEvents: 'none'
                }} />
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <div>
                    <div style={{
                      fontSize: '14px',
                      color: 'rgba(139, 92, 246, 0.9)',
                      fontWeight: '600',
                      marginBottom: '4px',
                      letterSpacing: '-0.01em'
                    }}>
                      Color Coding
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: 'rgba(255, 255, 255, 0.6)',
                      lineHeight: '1.3'
                    }}>
                      {colorBySchool ? `Individual colors for ${selectedSchools.length} schools` : 'Standard colors by type'}
                    </div>
                  </div>
                  <button
                    onClick={onToggleColorMode}
                    style={{
                      background: colorBySchool ? 
                        'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)' : 
                        'rgba(255, 255, 255, 0.08)',
                      border: `1px solid ${colorBySchool ? '#8B5CF6' : 'rgba(255, 255, 255, 0.15)'}`,
                      borderRadius: '8px',
                      color: colorBySchool ? 'white' : 'rgba(255, 255, 255, 0.8)',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      padding: '8px 14px',
                      transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: colorBySchool ? 
                        '0 4px 12px rgba(139, 92, 246, 0.3)' : 
                        '0 2px 8px rgba(0, 0, 0, 0.1)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-1px)';
                      if (!colorBySchool) {
                        e.target.style.background = 'rgba(255, 255, 255, 0.12)';
                        e.target.style.color = 'rgba(255, 255, 255, 0.95)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0px)';
                      if (!colorBySchool) {
                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                        e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                      }
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,3C16.97,3 21,7.03 21,12C21,17 16.97,21 12,21C7.03,21 3,17 3,12C3,7.03 7.03,3 12,3M12,5C10.89,5 9.85,5.26 8.92,5.74L15.26,12.08C15.74,11.15 16,10.11 16,9C16,6.79 14.21,5 12,5M16.08,9.74C15.15,10.26 14.11,10.5 13,10.5C9.69,10.5 7,7.81 7,4.5C7,4.34 7,4.18 7.03,4.03C4.75,5.27 3.17,7.91 3.17,10.91C3.17,15.06 6.61,18.5 10.76,18.5C13.76,18.5 16.4,16.92 17.64,14.64C17.82,14.18 17.96,13.7 18.03,13.2C17.5,11.81 16.08,9.74 16.08,9.74Z"/>
                    </svg>
                    {colorBySchool ? 'School Colors' : 'Type Colors'}
                  </button>
                </div>

                {/* PREMIUM: Enhanced Color Preview */}
                {(colorBySchool || selectedSchools.length >= 2) && (
                  <div style={{
                    marginTop: '12px',
                    minHeight: colorBySchool && selectedSchools.length > 0 ? 'auto' : '0',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                    opacity: colorBySchool ? 1 : 0,
                    position: 'relative',
                    zIndex: 1
                  }}>
                    {colorBySchool && selectedSchools.length > 0 && (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        maxHeight: '140px',
                        overflowY: 'auto',
                        padding: '8px',
                        background: 'rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.05)'
                      }}>
                        {selectedSchools.slice(0, 8).map((school, index) => {
                          const schoolKey = school.schoolKey || createSchoolKey(school.name, school.type);
                          const color = schoolColorMap.get(schoolKey) || schoolColors[index % schoolColors.length];
                          return (
                            <div key={school.id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '4px 6px',
                              borderRadius: '6px',
                              background: 'rgba(255, 255, 255, 0.02)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'rgba(255, 255, 255, 0.02)';
                            }}
                            >
                              <div style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
                                boxShadow: `0 0 8px ${color}60, inset 0 1px 0 rgba(255, 255, 255, 0.2)`,
                                flexShrink: 0,
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                              }}></div>
                              <span style={{
                                fontSize: '10px',
                                color: 'rgba(255, 255, 255, 0.8)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                fontWeight: '500'
                              }}>
                                {school.name}
                              </span>
                              <span style={{
                                fontSize: '8px',
                                color: 'rgba(255, 255, 255, 0.5)',
                                background: 'rgba(255, 255, 255, 0.08)',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                flexShrink: 0,
                                fontWeight: '600',
                                letterSpacing: '0.5px'
                              }}>
                                {school.type === 'elementary' ? 'ELEM' : school.type === 'middleSchool' ? 'MID' : 'HIGH'}
                              </span>
                            </div>
                          );
                        })}
                        {selectedSchools.length > 8 && (
                          <div style={{
                            fontSize: '9px',
                            color: 'rgba(255, 255, 255, 0.5)',
                            textAlign: 'center',
                            fontStyle: 'italic',
                            padding: '4px',
                            background: 'rgba(255, 255, 255, 0.02)',
                            borderRadius: '4px',
                            border: '1px dashed rgba(255, 255, 255, 0.1)'
                          }}>
                            +{selectedSchools.length - 8} more schools
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* PREMIUM: Layer Controls Section with Enhanced Glass Card */}
          <div style={{
            margin: '16px 20px',
            padding: '20px',
            background: `
              linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%),
              linear-gradient(to bottom, rgba(139, 92, 246, 0.02) 0%, transparent 70%)
            `,
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            boxShadow: `
              0 8px 32px rgba(0, 0, 0, 0.2),
              inset 0 1px 0 rgba(255, 255, 255, 0.03)
            `,
            opacity: isFiltering ? 0.5 : 1,
            transition: 'opacity 0.3s ease',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Card glow effect */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(139, 92, 246, 0.3) 50%, transparent 100%)'
            }} />
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                background: isFiltering ? 
                  'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)' : 
                  'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                boxShadow: isFiltering ? 
                  '0 4px 12px rgba(107, 114, 128, 0.3)' : 
                  '0 4px 12px rgba(139, 92, 246, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                  <path d="M3 4h18v2H3V4zm0 7h18v2H3v-2zm0 7h18v2H3v-2z"/>
                </svg>
              </div>
              <div>
                <h4 style={{
                  margin: '0',
                  fontSize: '16px',
                  fontWeight: '700',
                  background: isFiltering ? 
                    'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)' : 
                    'linear-gradient(135deg, #ffffff 0%, #e5e7eb 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  transition: 'all 0.3s ease'
                }}>
                  Layer Visibility
                </h4>
                <p style={{
                  margin: '2px 0 0 0',
                  fontSize: '12px',
                  color: isFiltering ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.5)',
                  fontWeight: '400',
                  transition: 'color 0.3s ease'
                }}>
                  Toggle map layers
                </p>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { key: 'elementary', label: 'Elementary Schools', color: '#3B82F6', icon: 'E' },
                { key: 'highSchool', label: 'High Schools', color: '#EF4444', icon: 'H' },
                { key: 'middleSchool', label: 'Middle Schools', color: '#10B981', icon: 'M' }
              ].map(({ key, label, color, icon }) => (
                <label key={key} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: isFiltering ? 'not-allowed' : 'pointer',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  background: visibleLayers[key] ? 
                    `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)` : 
                    'rgba(255, 255, 255, 0.02)',
                  border: `1px solid ${visibleLayers[key] ? `${color}40` : 'rgba(255, 255, 255, 0.05)'}`,
                  boxShadow: visibleLayers[key] ? 
                    `0 4px 12px ${color}20, inset 0 1px 0 rgba(255, 255, 255, 0.05)` : 
                    '0 2px 8px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  if (!isFiltering) {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = visibleLayers[key] ? 
                      `0 6px 20px ${color}30, inset 0 1px 0 rgba(255, 255, 255, 0.08)` : 
                      '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isFiltering) {
                    e.target.style.transform = 'translateY(0px)';
                    e.target.style.boxShadow = visibleLayers[key] ? 
                      `0 4px 12px ${color}20, inset 0 1px 0 rgba(255, 255, 255, 0.05)` : 
                      '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }
                }}
                >
                  {/* Subtle shimmer effect for active layers */}
                  {visibleLayers[key] && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: `linear-gradient(90deg, transparent 0%, ${color}20 50%, transparent 100%)`,
                      animation: 'shimmer-slow 4s ease-in-out infinite',
                      pointerEvents: 'none'
                    }} />
                  )}
                  
                  <input
                    type="checkbox"
                    checked={visibleLayers[key]}
                    onChange={() => !isFiltering && onToggleLayer(key)}
                    disabled={isFiltering}
                    style={{ display: 'none' }}
                  />
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '6px',
                    background: visibleLayers[key] ? 
                      (isFiltering ? '#6B7280' : `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`) : 
                      'rgba(255, 255, 255, 0.08)',
                    border: `2px solid ${visibleLayers[key] ? 
                      (isFiltering ? '#6B7280' : color) : 
                      'rgba(255, 255, 255, 0.15)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                    boxShadow: visibleLayers[key] ? 
                      `0 4px 8px ${color}40, inset 0 1px 0 rgba(255, 255, 255, 0.2)` : 
                      '0 2px 4px rgba(0, 0, 0, 0.1)',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    {visibleLayers[key] && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="white" style={{
                        filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))'
                      }}>
                        <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                      </svg>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, position: 'relative', zIndex: 1 }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '8px',
                      background: isFiltering ? 
                        'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)' : 
                        `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      boxShadow: `0 4px 8px ${isFiltering ? '#6B728040' : color + '40'}`,
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: '700'
                    }}>
                      {icon}
                    </div>
                    <span style={{ 
                      color: isFiltering ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.9)', 
                      fontSize: '14px', 
                      fontWeight: '600',
                      transition: 'color 0.3s ease',
                      letterSpacing: '-0.01em'
                    }}>
                      {label}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* PREMIUM: Advertisement Space */}
          <div style={{
            margin: '16px 20px',
            padding: '20px',
            background: `
              linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(245, 158, 11, 0.02) 100%),
              linear-gradient(to bottom, rgba(255, 255, 255, 0.01) 0%, transparent 100%)
            `,
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            border: '1px solid rgba(245, 158, 11, 0.1)',
            boxShadow: `
              0 8px 32px rgba(0, 0, 0, 0.15),
              inset 0 1px 0 rgba(255, 255, 255, 0.03)
            `,
            position: 'relative',
            overflow: 'hidden',
            minHeight: '120px'
          }}>
            {/* Ambient glow */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(245, 158, 11, 0.4) 50%, transparent 100%)'
            }} />
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <div>
                <h4 style={{
                  margin: '0',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'rgba(245, 158, 11, 0.9)'
                }}>
                  Support This Project
                </h4>
                <p style={{
                  margin: '2px 0 0 0',
                  fontSize: '11px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontWeight: '400'
                }}>
                  Keep the tool free & updated
                </p>
              </div>
            </div>
            
            {/* AdSense Placeholder */}
            <div style={{
              width: '100%',
              height: '80px',
              background: `
                linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%),
                repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 10px,
                  rgba(245, 158, 11, 0.05) 10px,
                  rgba(245, 158, 11, 0.05) 20px
                )
              `,
              borderRadius: '10px',
              border: '1px dashed rgba(245, 158, 11, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Placeholder content - will be replaced by AdSense */}
              <div style={{
                textAlign: 'center',
                color: 'rgba(245, 158, 11, 0.6)',
                fontSize: '11px',
                fontWeight: '500',
                letterSpacing: '0.5px'
              }}>
                <div style={{ marginBottom: '4px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.7 }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                Advertisement Space
              </div>
              
              {/* Shimmer effect */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent 0%, rgba(245, 158, 11, 0.1) 50%, transparent 100%)',
                animation: 'shimmer-slow 3s ease-in-out infinite',
                pointerEvents: 'none'
              }} />
            </div>
          </div>

          {/* PREMIUM: Address Search Section */}
          <div style={{
            margin: '16px 20px 24px 20px',
            padding: '20px',
            background: `
              linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%),
              linear-gradient(to bottom, rgba(245, 158, 11, 0.02) 0%, transparent 70%)
            `,
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            boxShadow: `
              0 8px 32px rgba(0, 0, 0, 0.2),
              inset 0 1px 0 rgba(255, 255, 255, 0.03)
            `,
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Card glow effect */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(245, 158, 11, 0.3) 50%, transparent 100%)'
            }} />
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
              </div>
              <div>
                <h4 style={{
                  margin: '0',
                  fontSize: '16px',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #ffffff 0%, #e5e7eb 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Address Lookup
                </h4>
                <p style={{
                  margin: '2px 0 0 0',
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontWeight: '400'
                }}>
                  Find school assignments
                </p>
              </div>
            </div>
            <AddressLookup
              map={map}
              currentYear={currentYear}
              yearData={yearData}
              selectedSchoolNames={selectedSchoolNames}
              isFiltering={isFiltering}
              onAddressFound={(marker, assignments) => {
                console.log('Address found:', marker, assignments);
              }}
              onAddressCleared={() => {
                console.log('Address cleared');
              }}
              sidebar={true}
            />
          </div>
          
          <style>{`
            @keyframes shimmer-slow {
              0% { transform: translateX(-100%); }
              50% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
            
            /* Custom scrollbar styles */
            ::-webkit-scrollbar {
              width: 6px;
            }
            ::-webkit-scrollbar-track {
              background: rgba(255, 255, 255, 0.02);
              border-radius: 3px;
            }
            ::-webkit-scrollbar-thumb {
              background: rgba(255, 255, 255, 0.1);
              border-radius: 3px;
            }
            ::-webkit-scrollbar-thumb:hover {
              background: rgba(255, 255, 255, 0.15);
            }
          `}</style>
        </div>

        {/* PREMIUM: Enhanced Collapsed Sidebar Mini Controls */}
        {sidebarCollapsed && (
          <div style={{
            position: 'absolute',
            top: '120px',
            left: '12px',
            right: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            opacity: sidebarCollapsed ? 1 : 0,
            transition: 'opacity 0.4s ease 0.15s',
            pointerEvents: sidebarCollapsed ? 'auto' : 'none'
          }}>
            {/* Mini Layer Toggles with Enhanced Design */}
            {[
              { key: 'elementary', color: '#3B82F6', icon: 'E' },
              { key: 'highSchool', color: '#EF4444', icon: 'H' },
              { key: 'middleSchool', color: '#10B981', icon: 'M' }
            ].map(({ key, color, icon }) => (
              <button
                key={key}
                onClick={() => onToggleLayer(key)}
                style={{
                  width: '40px',
                  height: '40px',
                  background: visibleLayers[key] ? 
                    `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)` : 
                    'rgba(255, 255, 255, 0.08)',
                  border: `1px solid ${visibleLayers[key] ? color : 'rgba(255, 255, 255, 0.15)'}`,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  color: visibleLayers[key] ? 'white' : 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  fontWeight: '700',
                  opacity: isFiltering ? 0.4 : 1,
                  pointerEvents: isFiltering ? 'none' : 'auto',
                  boxShadow: visibleLayers[key] ? 
                    `0 6px 20px ${color}40, inset 0 1px 0 rgba(255, 255, 255, 0.2)` : 
                    '0 4px 12px rgba(0, 0, 0, 0.2)',
                  backdropFilter: 'blur(8px)'
                }}
                onMouseEnter={(e) => {
                  if (!isFiltering) {
                    e.target.style.transform = 'scale(1.08) translateY(-2px)';
                    e.target.style.boxShadow = visibleLayers[key] ? 
                      `0 8px 25px ${color}50, inset 0 1px 0 rgba(255, 255, 255, 0.3)` : 
                      '0 6px 18px rgba(0, 0, 0, 0.3)';
                    if (!visibleLayers[key]) {
                      e.target.style.background = 'rgba(255, 255, 255, 0.12)';
                    }
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isFiltering) {
                    e.target.style.transform = 'scale(1) translateY(0px)';
                    e.target.style.boxShadow = visibleLayers[key] ? 
                      `0 6px 20px ${color}40, inset 0 1px 0 rgba(255, 255, 255, 0.2)` : 
                      '0 4px 12px rgba(0, 0, 0, 0.2)';
                    if (!visibleLayers[key]) {
                      e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                    }
                  }
                }}
                title={`Toggle ${key === 'elementary' ? 'Elementary' : key === 'highSchool' ? 'High' : 'Middle'} Schools`}
              >
                {icon}
              </button>
            ))}

            {/* Mini Filter Indicator with Enhanced Design */}
            {isFiltering && (
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '6px',
                boxShadow: `
                  0 6px 20px rgba(16, 185, 129, 0.4),
                  inset 0 1px 0 rgba(255, 255, 255, 0.2)
                `,
                backdropFilter: 'blur(8px)',
                animation: 'pulse-glow 2s ease-in-out infinite'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
                </svg>
              </div>
            )}
            
            <style>{`
              @keyframes pulse-glow {
                0%, 100% { 
                  box-shadow: 
                    0 6px 20px rgba(16, 185, 129, 0.4),
                    inset 0 1px 0 rgba(255, 255, 255, 0.2);
                }
                50% { 
                  box-shadow: 
                    0 8px 30px rgba(16, 185, 129, 0.6),
                    inset 0 1px 0 rgba(255, 255, 255, 0.3);
                }
              }
            `}</style>
          </div>
        )}
      </div>

      {/* Return sidebar width for parent component positioning */}
      <div style={{ display: 'none' }} data-sidebar-width={sidebarWidth} />
    </>
  );
};

export default Sidebar;